import { LUX_PLUS_MEMBER_PRICES_FILTER } from 'lib/experiences/luxPlusFeatureFilterUtils'
import { experienceCategoryVisionSlug } from 'lib/experiences/experienceSearchStreaming'

const EXPERIENCE_TIME_SLOTS: Array<Experiences.TimeSlotFilter> = ['all-day', 'morning', 'afternoon', 'evening']

const EMPTY_STREAM_FACETS: App.ExperienceFacets = {
  category: [],
  collection: [],
  areas: [],
  price: { min: 0, max: 0 },
  leExclusiveCount: 0,
  freeCancellationCount: 0,
  instantConfirmationCount: 0,
  giftingAvailableCount: 0,
  luxPlusMemberPriceCount: 0,
  ratingCounts: [],
  targetAudienceCounts: [],
  timeSlotCounts: [],
}

export function isExperienceStreamFiltersV2(value: unknown): value is App.ExperienceStreamFiltersV2 {
  return !!value && typeof value === 'object' && Array.isArray((value as App.ExperienceStreamFiltersV2).categories)
}

function featureCount(features: Array<App.ExperienceStreamFilterValue> | undefined, slug: string): number {
  return features?.find(feature => feature.slug === slug)?.count ?? 0
}

function buildCategoryIdBySlug(categories: Array<App.ExperienceItemCategory>): Map<string, number> {
  const categoryById = new Map(categories.map(category => [Number(category.id), category]))
  const categoryIdBySlug = new Map<string, number>()

  for (const category of categories) {
    if (!category.name) continue
    const slug = experienceCategoryVisionSlug(category.name)
    categoryIdBySlug.set(slug, Number(category.id))

    if (category.level === 'child' && category.parentId) {
      const parentName = categoryById.get(Number(category.parentId))?.name
      if (!parentName) continue
      categoryIdBySlug.set(`${experienceCategoryVisionSlug(parentName)}:${slug}`, Number(category.id))
    }
  }

  return categoryIdBySlug
}

function mapCategoryId(slug: string, categoryIdBySlug: Map<string, number>): number | undefined {
  const exact = categoryIdBySlug.get(slug)
  if (exact != null) return exact
  const childSlug = slug.includes(':') ? slug.slice(slug.lastIndexOf(':') + 1) : undefined
  return childSlug ? categoryIdBySlug.get(childSlug) : undefined
}

export function mapExperienceStreamFiltersToFacets(
  filtersV2: unknown,
  categories: Array<App.ExperienceItemCategory> = [],
): App.ExperienceFacets {
  if (!isExperienceStreamFiltersV2(filtersV2)) {
    return EMPTY_STREAM_FACETS
  }

  const categoryIdBySlug = buildCategoryIdBySlug(categories)
  const category = (filtersV2.categories ?? []).flatMap(item => {
    const categoryId = mapCategoryId(item.slug, categoryIdBySlug)
    if (categoryId == null) return []
    return [{ categoryId, count: item.count }]
  })

  const memberPrice = filtersV2.luxPlusExclusive?.find(item =>
    item.slug === LUX_PLUS_MEMBER_PRICES_FILTER
    || item.label === LUX_PLUS_MEMBER_PRICES_FILTER
    || experienceCategoryVisionSlug(item.label) === 'member_prices',
  )

  return {
    category,
    collection: (filtersV2.collections ?? []).map(item => ({
      collectionId: item.slug,
      theme: item.label,
      count: item.count,
    })),
    areas: [],
    price: filtersV2.price ?? { min: 0, max: 0 },
    dateRange: filtersV2.dateRange,
    leExclusiveCount: featureCount(filtersV2.features, 'le_exclusive'),
    freeCancellationCount: featureCount(filtersV2.features, 'free_cancellation'),
    instantConfirmationCount: featureCount(filtersV2.features, 'instant_confirmation'),
    giftingAvailableCount: featureCount(filtersV2.features, 'gifting_available'),
    luxPlusMemberPriceCount: memberPrice?.count ?? 0,
    ratingCounts: (filtersV2.customerRatings ?? []).flatMap(item => {
      const threshold = Number(item.slug)
      if (!Number.isFinite(threshold)) return []
      return [{ threshold, count: item.count }]
    }),
    targetAudienceCounts: (filtersV2.targetAudience ?? []).flatMap(item => {
      if (!item.slug) return []
      return [{ audience: item.slug as App.ExperienceTargetAudience, count: item.count }]
    }),
    timeSlotCounts: (filtersV2.timeSlots ?? []).flatMap(item => {
      if (!EXPERIENCE_TIME_SLOTS.includes(item.slug as Experiences.TimeSlotFilter)) return []
      return [{ slot: item.slug as Experiences.TimeSlotFilter, count: item.count }]
    }),
  }
}
